import "./RadarChart.css";

import { Filler } from "chart.js";
import { ComponentProps, useMemo } from "react";
import { Radar } from "react-chartjs-2";

import { ColorConstants } from "../../../constants";
import { maybeSetGlobalChartJsConfiguration } from "../utils";
import { preloadIcons, RadarPointLabelIcons } from "./RadarChartUtils";

maybeSetGlobalChartJsConfiguration();

export interface RadarChartProps {
  labels: string[];
  icons?: string[];
  iconSize?: number;
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor: string;
    borderDash?: number[];
  }[];
  emptyPlaceholder?: string;
}

export const RadarChart = ({
  labels,
  icons,
  iconSize,
  datasets,
  emptyPlaceholder = "No data to display",
}: RadarChartProps): JSX.Element => {
  if (!datasets || Object.keys(datasets).length === 0 || !labels || Object.keys(labels).length === 0)
    return (
      <div className="RadarChart RadarChartEmpty">
        <p className="body3">{emptyPlaceholder}</p>
      </div>
    );
  const data: ComponentProps<typeof Radar>["data"] = {
    labels,
    datasets,
  };
  const options: ComponentProps<typeof Radar>["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: 4,
    elements: {
      point: {
        radius: 0,
        hitRadius: 0,
      },
    },
    plugins: {
      legend: {
        display: false,
        labels: {
          usePointStyle: true,
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      r: {
        grid: {
          color: ColorConstants.INDIGO_100,
        },
        angleLines: {
          display: true,
          color: ColorConstants.INDIGO_100,
        },
        suggestedMin: 0,
        suggestedMax: 100,
        ticks: {
          display: false,
          maxTicksLimit: 10,
          color: ColorConstants.INDIGO_100,
        },
        pointLabels: {
          display: !icons,
        },
      },
    },
    layout: {
      padding: 60,
    },
  };

  const plugins = useMemo(() => {
    const tempPlugins = [Filler];

    if (icons && iconSize) {
      const images = preloadIcons(icons);
      tempPlugins.push(RadarPointLabelIcons(images, iconSize));
    }

    return tempPlugins;
  }, [datasets]);

  return (
    <div className="RadarChart">
      <Radar data={data} options={options} plugins={plugins} />
    </div>
  );
};
